import { UpdatePurpose, ReviewType } from "../../../../../components/financialTables/configuration/constants"
import Assert from "../../../../../utils/asserts"
import { ConfigProps } from "./constants"

export function getTableTypes(performanceKpis) {

    const tableTypes = new Set()
    const entities = [...performanceKpis.updates, ...performanceKpis.reviews]
    entities.map(c => c.tableType)
        .forEach(t => tableTypes.add(t))

    return Array.from(tableTypes)
}
const REVIEW_TYPE_W_KEY_COMPONENT = {
    [ReviewType.FULL_CHECK]: "FC",
    [ReviewType.CORE_DATA_CHECK]: "CD",
    [ReviewType.VISUAL_CHECK]: "VC",
}

const TABLE_TYPE_W_KEY_COMPONENT = {
    "KPIs": "KPI",
    "Income Statement": "IS",
}

function getWeightForTableReview(config, tableType, reviewType) {

    const reviewTypePrefix = REVIEW_TYPE_W_KEY_COMPONENT[reviewType]
    Assert.notNullOrUndefined(reviewTypePrefix, "getWeightForTableReview:" + reviewType)

    const tableTypeComponent = TABLE_TYPE_W_KEY_COMPONENT[tableType] || "OTHER"

    const weight = config[reviewTypePrefix + "_" + tableTypeComponent + "_PER_CELL_W"]
    Assert.notNullOrUndefined(weight, "getWeightForTableReview")

    return weight
}


function getWeightForTableUpdate(config, tableType, updatePurpose) {
    let weight
    if (tableType === "KPIs") {
        weight = config[ConfigProps.UPDATE_KPI_PER_CELL_W]
    } else if (tableType === "Income Statement") {
        weight = config[ConfigProps.UPDATE_IS_PER_CELL_W]
    } else {
        weight = config[ConfigProps.UPDATE_OTHER_PER_CELL_W]
    }
     if (updatePurpose === UpdatePurpose.MISTAKES_DETECTION) {
         weight = weight * config[ConfigProps.MD_UPDATE_SCALE]
     }
     return weight
}


function accumulateUpdatesPerformance(userToPerformanceKPIs, performance, config) {
    const tableTypes = config[ConfigProps.TABLE_TYPES]
    const updates = performance.updates
        .filter(u => tableTypes.includes(u.tableType))
        .filter(u => u.cellsScope !== null)
        .filter(u => userToPerformanceKPIs[u.userId] !== undefined)
        .filter(u => u.purpose !== UpdatePurpose.MISTAKES_HOLDER)

    for (const updatesPerformance of updates) {
        const tableType = updatesPerformance.tableType
        const weight = getWeightForTableUpdate(config, tableType, updatesPerformance.purpose)

        const score = updatesPerformance.cellsScope * weight
        if (updatesPerformance.purpose === UpdatePurpose.MISTAKES_DETECTION) {
            userToPerformanceKPIs[updatesPerformance.userId].mistakesDetectionUpdates += score
        } else {
            userToPerformanceKPIs[updatesPerformance.userId].regularUpdates += score
        }
    }
}

function accumulateReviewsPerformance(userToPerformanceKPIs, performance, config) {
    const tableTypes = config[ConfigProps.TABLE_TYPES]

    const reviews = performance.reviews
        .filter(r => tableTypes.includes(r.tableType))
        .filter(u => u.cellsScope !== null)
        .filter(u => userToPerformanceKPIs[u.userId] !== undefined)

    for (const reviewsPerformance of reviews) {
        const tableType = reviewsPerformance.tableType
        const weight = getWeightForTableReview(config, tableType, reviewsPerformance.type)

        const score = reviewsPerformance.cellsScope * weight
        if (reviewsPerformance.type === ReviewType.FULL_CHECK) {
            userToPerformanceKPIs[reviewsPerformance.userId].fullReviews += score
        } else if (reviewsPerformance.type === ReviewType.CORE_DATA_CHECK && reviewsPerformance.updatePurpose === UpdatePurpose.MISTAKES_DETECTION) {
            userToPerformanceKPIs[reviewsPerformance.userId].coreDataReviews += score
        } else if (reviewsPerformance.type === ReviewType.CORE_DATA_CHECK && reviewsPerformance.updatePurpose === UpdatePurpose.REGULAR_UPDATE) {
            userToPerformanceKPIs[reviewsPerformance.userId].incrementalCoreDataReviews += score
        } else {
            Assert.trueVal(reviewsPerformance.type === ReviewType.VISUAL_CHECK)
            userToPerformanceKPIs[reviewsPerformance.userId].visualChecks += score
        }
    }
}


function calculateTotal(userToPerformanceKPIs) {
    Object.values(userToPerformanceKPIs).forEach(perf => {
        perf.totalScore = perf.mistakesDetectionUpdates +
            perf.regularUpdates + perf.fullReviews + perf.coreDataReviews + perf.incrementalCoreDataReviews +
            perf.visualChecks
    })
}

function calculateMistakesAdjustedTotal(userToPerformanceKPIs, mistakeAjustedKPIs, config) {
    Object.keys(userToPerformanceKPIs).forEach(userId => {
        const userPerformance = userToPerformanceKPIs[userId]
        const userMistakes = mistakeAjustedKPIs[userId]

        const mistakesDetectionUpdates = adjustForMistakes(
            userPerformance.mistakesDetectionUpdates, userMistakes.updatesScore,
            config[ConfigProps.T_MISTAKES_FOR_R_UPDATE])

        const regularUpdates = adjustForMistakes(userPerformance.regularUpdates,
            userMistakes.updatesScore, config[ConfigProps.T_MISTAKES_FOR_R_UPDATE])

        const fullReviews = adjustForMistakes(userPerformance.fullReviews,
            userMistakes.fullReviewsScore, config[ConfigProps.T_MISTAKES_FOR_F_C_REVIEW])

        const coreDataReviews = adjustForMistakes(userPerformance.coreDataReviews,
            userMistakes.coreDataReviewsScore, config[ConfigProps.T_MISTAKES_FOR_C_D_C_REVIEW])

        const incrementalCoreDataReviews = adjustForMistakes(userPerformance.incrementalCoreDataReviews,
            userMistakes.incrementalCoreDataReviewsScore, config[ConfigProps.T_MISTAKES_FOR_C_D_C_REVIEW])

        userPerformance.totalScoreMistakesAdjusted = mistakesDetectionUpdates +
            regularUpdates + fullReviews + coreDataReviews + incrementalCoreDataReviews +
            userPerformance.visualChecks
    })
}


export function adjustForMistakes(productivityScore, mistakeScore, mistakesScoreMaxTarget) {
    Assert.typeNumber(productivityScore)
    Assert.assertTrue(() => typeof mistakeScore === "number" || mistakeScore === undefined)
    mistakeScore = mistakeScore === undefined ? 0 : mistakeScore
    mistakesScoreMaxTarget = Number(mistakesScoreMaxTarget)

    const divider = (mistakeScore <= mistakesScoreMaxTarget) ? 1 : (mistakeScore / mistakesScoreMaxTarget)
    return productivityScore / divider
}

export function getQuantityPerformance(performance, usersMap, config, mistakeAjustedKPIs) {
    const userToPerformanceKPIs = {}
    for (const userId of Object.keys(usersMap)) {
        userToPerformanceKPIs[userId] = {
            regularUpdates: 0,
            mistakesDetectionUpdates: 0,
            fullReviews: 0,
            /** 
            * `coreDataReviews` is used for update with a purpose of `MISTAKES_DETECTION`
            */
            coreDataReviews: 0,
            visualChecks: 0,
            /** 
             * `incrementalCoreDataReviews` is used for update with a purpose of `REGULAR_UPDATE`
             */
            incrementalCoreDataReviews: 0,
        }
    }

    accumulateUpdatesPerformance(userToPerformanceKPIs, performance, config)
    accumulateReviewsPerformance(userToPerformanceKPIs, performance, config)
    calculateTotal(userToPerformanceKPIs)
    calculateMistakesAdjustedTotal(userToPerformanceKPIs, mistakeAjustedKPIs, config)

    return userToPerformanceKPIs
}

export function getTableData(userToPerformanceKPIs) {
    const tableData = Object.keys(userToPerformanceKPIs)
        .map(userId => {
            const userKnowledge = userToPerformanceKPIs[userId]
            return { userId, ...userKnowledge }
        })
        .sort((a, b) => b.totalScore - a.totalScore)

    const total = {
        regularUpdates: 0, mistakesDetectionUpdates: 0, fullReviews: 0,
        coreDataReviews: 0, incrementalCoreDataReviews: 0, visualChecks: 0, totalScore: 0,
        totalScoreMistakesAdjusted: 0
    }
    for (const row of tableData) {
        for (const key of Object.keys(total)) {
            total[key] += row[key]
        }
    }
    total.userId = "TOTAL"
    tableData.push(total)
    return tableData
}